// WHO IS DRIVING THE ENGINEER PWA (Session AGV; the engineer-row test added by Session AGW).
//
// The engineer PWA is reachable by TWO kinds of account, and the difference is not cosmetic:
//
//   ENGINEER   -- signs in at /engineer/login with a username and password. Has an Engineer row,
//                 so the PERSONAL surfaces exist for them: their own timesheet, their own message
//                 thread with the office, their own SMS preference, their own van stock.
//
//   OVERSIGHT  -- an admin (or office) account, signed in through the office Google flow. The
//                 route guard admits it deliberately (App.tsx ProtectedRoute allow={["admin",
//                 "engineer"]}), and the server gives it the whole account on the SHARED surfaces:
//                 every job (job.routes.ts branches on role first), every site and every record
//                 (engineerScope's OVERSIGHT_ROLES). That is the Director walking the PWA to see
//                 what his engineers see, and it works.
//
// THE POINT OF THIS FILE: before Session AGV the four personal surfaces did not know which kind of
// account they were serving, so each fired a request that could only ever fail and each failed
// DIFFERENTLY -- the Director's live session on 4 August 2026 produced 400 on the timesheet, 404 on
// the message thread, 403 on the SMS preference and 404 (seven times) on van stock. Four opaque HTTP
// errors on one screen read as "the app is broken" when the truth was simply that his account had no
// engineer records. So these surfaces ASK FIRST and say so plainly.
//
// WHY THERE ARE TWO QUESTIONS HERE AND NOT ONE. Because the SERVER has two different rules, and one
// helper answering both would have to be wrong about one of them:
//
//   OWN ENGINEER RECORDS (timesheet, van stock) are keyed on the ENGINEER ROW, which hangs off
//   userId and NOT off role -- timesheet.routes.ts resolves "my timesheet" by asking whether the
//   caller has a row ("resolve it by FACT rather than by role"), and vanStock.routes.ts
//   engineerIdForUser does the same with no role filter at all. So an admin who holds an Engineer
//   row genuinely HAS these, and asking works.
//
//   AN OWN MESSAGE THREAD needs role engineer, and that is not an oversight to be tidied up later.
//   A thread is one engineer and the office; message.routes.ts resolves the far side with
//   role: UserRole.engineer, and validateMessagePairing refuses office-to-office outright ("The
//   office can only message engineers here"). An admin walking the PWA IS the office, so there is no
//   counterparty and no thread of their own -- an Engineer row does not change that. Giving this
//   surface the row test would send it straight back to the 404 Session AGV fixed.
//
// Session AGW is why the distinction now matters: it gave the Director's admin account an Engineer
// row, so the case this file previously called "deliberate and, in this tenant, unreachable" became
// reachable. The fix is the one this file named at the time -- "it would need a field on the session
// user, not a cleverer guess here" -- so GET /api/auth/me now returns hasEngineerRecord and the row
// test reads it. Note what is still NOT done: "admin" is not added to any role list, because that
// would show EVERY admin a timesheet screen with nothing to put in it.
//
// Nothing here grants access. It is only ever used to decide what to SHOW, and the server remains
// the authority on every read -- each of those routes re-resolves the row for itself.
//
import type { User } from "./api";

type PwaIdentity = Pick<User, "role" | "hasEngineerRecord"> | null | undefined;

// Roles that drive the PWA for oversight rather than to do the work. Mirrors OVERSIGHT_ROLES in
// server/src/modules/engineer/scope.ts. `office` cannot in fact reach /api/jobs/* or
// /api/engineer/* (both guards are admin+engineer), but one rule that is right on both sides beats
// two that drift.
const OVERSIGHT_ROLES = new Set(["admin", "office"]);

// Does this account have personal engineer records of its own -- a timesheet, a van? An engineer
// always does. Any other account does only if it holds an Engineer row, which is a fact the server
// establishes and hands over on the session user; absent (an older server, or a User built in a
// test) it falls back to the role, which is the pre-AGW behaviour.
export function hasOwnEngineerRecords(user: PwaIdentity): boolean {
  return user?.role === "engineer" || user?.hasEngineerRecord === true;
}

// Does this account have its own message thread with the office? Only an engineer: the office
// cannot hold a thread with itself. Deliberately does NOT consult hasEngineerRecord -- see the note
// above. Kept as its own function so the reason lives next to the rule instead of being rediscovered
// as a bug.
export function hasOwnMessageThread(user: PwaIdentity): boolean {
  return user?.role === "engineer";
}

// Is this an admin or office account walking the PWA? Used only to word the explanation, so the
// panel can name the office page that DOES hold what they are looking for.
export function isOversightAccount(user: PwaIdentity): boolean {
  return !!user && OVERSIGHT_ROLES.has(user.role);
}
